/**
 * TaskGroupHeader Component
 *
 * Feature: 650-task-grouping
 *
 * Displays the collapsed group view with:
 * - Expand/collapse chevron
 * - Group label (e.g., "Rate photos")
 * - Progress indicator (e.g., "2 of 4 completed")
 * - Progress bar
 * - "Go to Critique" navigation button
 *
 * Note: Hero image is now rendered by TaskGroupHeroImage at TaskGroup level
 * for full-height display.
 */

import { Link } from 'react-router-dom';
import type { TaskGroupHeaderProps } from '@/types/task-groups';

export function TaskGroupHeader({
  group,
  isExpanded,
  onToggle,
  onNavigate,
  sessionTitle,
}: TaskGroupHeaderProps) {
  // Build display label: "Rate photos on <Session Title>" or just "Rate photos" if no session title
  const displayLabel = sessionTitle ? `${group.label} on ${sessionTitle}` : group.label;

  return (
    <div className="flex items-center gap-4">
      {/* Left: Expand/Collapse + Label + Progress Count */}
      <button
        onClick={onToggle}
        className="flex items-center gap-3 text-left flex-1 min-w-0"
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${displayLabel}`}
      >
        {/* Chevron Right icon - rotates when expanded */}
        <svg
          className={`w-4 h-4 flex-shrink-0 text-muted transition-transform duration-200 ${
            isExpanded ? 'rotate-90' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        <div className="min-w-0 flex-1">
          <span className="font-medium text-foreground">{displayLabel}</span>
          <span className="ml-2 text-sm text-muted">
            {group.completedCount} of {group.totalCount} completed
          </span>
        </div>
      </button>

      {/* Center: Progress Bar */}
      <div
        className="w-24 h-2 bg-surface-3 rounded-full overflow-hidden flex-shrink-0"
        role="progressbar"
        aria-valuenow={group.progressPercent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${group.progressPercent}% complete`}
      >
        <div
          className="h-full bg-primary transition-all duration-300"
          style={{ width: `${group.progressPercent}%` }}
        />
      </div>

      {/* Right: Navigation Button */}
      <Link
        to={group.url}
        onClick={(e) => {
          e.stopPropagation();
          onNavigate();
        }}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors flex-shrink-0"
      >
        Go to Critique
        {/* External Link icon */}
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </Link>
    </div>
  );
}
